import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, ShieldCheck, Lock, Mail, ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/context/AuthContext";
import { apiErrorMessage } from "@/lib/api";

export default function AdminLogin() {
  const nav = useNavigate();
  const { adminLogin } = useAuth();
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ email: "", password: "" });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await adminLogin(form.email, form.password);
      toast.success("Admin session opened");
      nav("/admin-portal/dashboard");
    } catch (err) {
      toast.error(apiErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#07111D] text-white flex items-center justify-center p-6">
      <div className="w-full max-w-md" data-testid="admin-login-panel">
        <div className="flex items-center gap-2 mb-8">
          <div className="w-10 h-10 rounded-lg border border-[#1E2D40] bg-[#101B28] flex items-center justify-center">
            <ShieldCheck className="w-5 h-5 text-[#18BFFF]" />
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-[0.2em] text-[#4A5A73]">Elite Digital · restricted</p>
            <h1 className="font-display text-xl font-semibold">Admin portal</h1>
          </div>
        </div>

        <form onSubmit={submit} className="space-y-4 rounded-2xl border border-[#1E2D40] bg-[#101B28] p-6">
          <Fld label="Email" icon={Mail}>
            <Input required type="email" data-testid="admin-input-email" value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              className={inputCls} placeholder="you@elite-digital.hr" autoComplete="email" />
          </Fld>
          <Fld label="Password" icon={Lock}>
            <Input required type="password" data-testid="admin-input-password" value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
              className={inputCls} placeholder="••••••••" autoComplete="current-password" />
          </Fld>
          <Button type="submit" disabled={busy} data-testid="admin-submit-btn"
                  className="w-full bg-[#18BFFF] text-[#07111D] hover:bg-[#4DD0FF] font-semibold h-11">
            {busy && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            Enter admin panel
          </Button>
        </form>

        <Link to="/login" className="mt-6 inline-flex items-center gap-1.5 text-xs text-[#4A5A73] hover:text-[#94A3B8] transition-colors" data-testid="back-to-login">
          <ArrowLeft className="w-3 h-3" />
          Back to client sign in
        </Link>
      </div>
    </div>
  );
}

const inputCls = "bg-[#0B131E] border-[#1E2D40] text-white placeholder:text-[#4A5A73] focus-visible:ring-[#18BFFF] focus-visible:border-[#18BFFF] h-11";

function Fld({ label, icon: Icon, children }) {
  return (
    <div>
      <Label className="text-xs uppercase tracking-[0.15em] text-[#94A3B8] flex items-center gap-1.5">
        <Icon className="w-3 h-3" /> {label}
      </Label>
      <div className="mt-1.5">{children}</div>
    </div>
  );
}
